import type { ReactElement, ReactNode } from 'react';
import {
  Children,
  cloneElement,
  isValidElement,
  useId,
  useRef,
  useState,
} from 'react';
import { useSelect } from 'downshift';

import KebabIcon from '../icons/KebabIcon';

import { usePositionedPopup } from '../../hooks/use-positioned-popup/use-positioned-popup';
import { Box } from '../box/box';
import { Button } from '../button/button';
import { Card } from '../card/card';
import { GrandEntrance } from '../grand-entrance/grand-entrance';

import {
  type ActionMenuItemButtonProps,
  type ActionMenuItemDisabledProps,
  type ActionMenuItemLinkProps,
  MenuItemButton,
  MenuItemDisabled,
  MenuItemLink,
} from './action-menu-items';

import * as styles from './action-menu.css';

export type { ActionMenuItemLinkProps };

type ActionMenuChildrenProps =
  | ActionMenuItemButtonProps
  | ActionMenuItemLinkProps;

export interface ActionMenuProps {
  // this is an accessible label so will only be used by screen readers
  triggerLabel: ReactNode;
  disabled?: boolean;
  children:
    | ReactElement<ActionMenuChildrenProps>
    | Array<ReactElement<ActionMenuChildrenProps> | null>;
}

export const ActionMenu = ({
  children,
  disabled = false,
  triggerLabel,
}: ActionMenuProps) => {
  const items = Children.toArray(children).filter(
    (child): child is ReactElement<ActionMenuChildrenProps> =>
      isValidElement(child) &&
      isValidElement<ActionMenuChildrenProps>(child) &&
      typeof child !== 'string' &&
      // remove hidden items
      !child.props.hide,
  );

  const menuRef = useRef<HTMLDivElement>(null);

  const id = useId();

  const {
    isOpen,
    getToggleButtonProps,
    getMenuProps,
    getItemProps,
    highlightedIndex,
  } = useSelect({
    id: `menu-item-${id}`,
    items,
    isItemDisabled: (item) => {
      // TODO: This is introduced to mitigate the issue when MenuItems are wrapped in an AccessControl
      // To determine whether the children is an AccessControl we check the access prop
      // Fragile, either we need tests or a better approach
      if (
        'access' in item.props &&
        isValidElement<ActionMenuChildrenProps>(item.props.children)
      ) {
        return item.props.children.props.disabled || false;
      }

      if (isValidElement<ActionMenuChildrenProps>(item)) {
        return item.props.disabled || false;
      }
      return isValidElement<ActionMenuItemDisabledProps>(item);
    },
    stateReducer: (state, { changes, type }) => {
      if (type === useSelect.stateChangeTypes.ToggleButtonKeyDownEnter) {
        const item = menuRef.current?.children[state.highlightedIndex];

        if (!item || !(item instanceof HTMLElement)) {
          throw new Error('Item undefined or the wrong type');
        }

        item.click();
        // returning state here, in case there are any issues with returning undefined
        return state;
      }

      // …otherwise default useSelect reducer behaviour
      return changes;
    },
  });

  // This feels like a false positive, but we might want to investigate
  // other alternatives
  // eslint-disable-next-line react-hooks/refs
  const [menuProps] = useState(() =>
    getMenuProps({ ref: menuRef, role: 'menu' }),
  );

  const { setFloating, setReference, floatingStyles, constrainStyles } =
    usePositionedPopup({
      open: isOpen,
      placement: 'bottom-end',
    });

  const { 'aria-activedescendant': ariaActivedescendant, ...toggleProps } =
    getToggleButtonProps({
      ref: setReference,
      role: 'button',
      // button will be labelled by the triggerLabel
      'aria-labelledby': undefined,
    });

  // Only include aria-activedescendant if it is a non-empty string
  // This is a workaround for a WCAG issue. Downshift can pass down an empty string
  // which is invalid
  const formattedToggleProps = {
    ...toggleProps,
    ...(ariaActivedescendant && ariaActivedescendant.length > 0
      ? { 'aria-activedescendant': ariaActivedescendant }
      : { 'aria-activedescendant': undefined }),
  };

  return (
    <Box>
      <Button
        {...formattedToggleProps}
        disabled={disabled}
        iconColor={
          disabled ? 'neutral300' : isOpen ? 'tertiary500' : 'neutral500'
        }
        Icon={KebabIcon}
        variant="icon"
      >
        {triggerLabel}
      </Button>

      <GrandEntrance
        className={styles.menuFloating}
        config={
          isOpen
            ? {
                mass: 1,
                tension: 260,
                friction: 10,
                precision: 0.01,
                clamp: true,
                velocity: 0.002, // seconds
              }
            : {
                duration: 0,
              }
        }
        show={isOpen}
      >
        <Box
          padding={{ y: 'baseNeg4' }}
          ref={setFloating}
          style={floatingStyles}
        >
          <Card
            padding="none"
            variant="lightShadow"
            stack="column"
            {...menuProps}
            style={constrainStyles}
          >
            {items.map((child, index) =>
              cloneElement(child, {
                ...getItemProps({
                  item: child,
                  index,
                  role: 'menuitem',
                  onClick:
                    'onClick' in child.props ? child.props.onClick : undefined,
                }),
                highlight: highlightedIndex === index,
              }),
            )}
          </Card>
        </Box>
      </GrandEntrance>
    </Box>
  );
};

ActionMenu.ItemButton = MenuItemButton;
ActionMenu.ItemLink = MenuItemLink;
ActionMenu.Disabled = MenuItemDisabled;
